/**
 * Taste Them All Bundle 2 Builder (IWTTE2)
 *
 * Maps two protein powder flavor selections to a single pre-built bundle
 * variant (10 total: every C(5,2) pair plus the four self-pairs). Customer
 * picks Powder #1 + Powder #2; this module finds the matching bundle
 * variant by SKU and adds ONE line item to /cart/add. The One-time /
 * Subscribe & Save toggle, validation slot, and CTA all live in the
 * bundle-buy-card snippet — this module drives that card via
 * window.paBundleBuyCard[uid]:
 *   - setEnabled / setValidationMessage when selections change
 *   - setVariant(matchedVariant.id) so the card displays the right
 *     compare_at strikethrough + sub price
 *   - onCtaClick(planId): post the matched variant with selling_plan
 *     when subscribed.
 *
 * Matching uses SKUs (not names) so renaming products won't break anything:
 * - Powder SKUs: PP600-CHOC, PP600-VAN, PP600-STR, PP600-NAT
 * - Bundle SKUs: PPPC-IWTTE2-CHOC-VAN (flavor codes sorted alphabetically)
 * - Extract flavor code from powder SKU → sort two codes → find bundle variant
 */

(function() {
  'use strict';

  window.TasteThemAllBundle2Builder = window.TasteThemAllBundle2Builder || {};

  class BundleBuilder {
    constructor(containerId) {
      this.containerId = containerId;
      this.container = document.getElementById(containerId);
      if (!this.container) {
        console.error('Taste Them All Bundle 2 Builder: Container not found:', containerId);
        return;
      }

      // Load data from window object
      this.data = window.tasteThemAllBundle2Data && window.tasteThemAllBundle2Data[containerId];
      if (!this.data) {
        console.error('Taste Them All Bundle 2 Builder: Data not found for:', containerId);
        return;
      }

      // State
      this.selectedPowderSku1 = null;
      this.selectedPowderSku2 = null;

      // DOM Elements (powder buttons + error slot only — buy UI is in
      // the bundle-buy-card snippet).
      this.variantButtons1 = this.container.querySelectorAll('[data-item="proteinPowder1"] .variant-btn');
      this.variantButtons2 = this.container.querySelectorAll('[data-item="proteinPowder2"] .variant-btn');
      this.errorMessage = this.container.querySelector('.taste-them-all-bundle-2-error-message');

      // Build combo map from bundle variant SKUs
      this.comboMap = this.buildComboMap();

      // bundle-buy-card API — connected lazily in init().
      this.cardApi = null;

      this.init();
    }

    init() {
      this.bindEvents();
      this.connectBuyCard(() => {
        this.validateSelections();
      });
    }

    /**
     * Wait briefly for the bundle-buy-card snippet's IIFE to register
     * its API, then run the callback. Resolves immediately if available.
     */
    connectBuyCard(cb) {
      var attempts = 0;
      var maxAttempts = 50; // 50 × 20ms = 1s
      var self = this;
      function tryConnect() {
        var api = window.paBundleBuyCard && window.paBundleBuyCard[self.containerId];
        if (api) {
          self.cardApi = api;
          api.onCtaClick(function (planId) { self.handleAddToCart(planId); });
          if (cb) cb();
          return;
        }
        if (++attempts < maxAttempts) {
          setTimeout(tryConnect, 20);
        }
      }
      tryConnect();
    }

    /**
     * Extract flavor code from a powder SKU.
     * E.g. "PP600-CHOC" → "CHOC", "PP600-VAN" → "VAN"
     */
    extractFlavorCode(powderSku) {
      if (!powderSku) return null;
      const parts = powderSku.split('-');
      return parts.length >= 2 ? parts[parts.length - 1] : null;
    }

    /**
     * Extract the two flavor codes from a bundle SKU.
     * E.g. "PPPC-IWTTE2-CHOC-VAN" → ["CHOC", "VAN"]
     */
    extractBundleFlavorCodes(bundleSku) {
      if (!bundleSku) return null;
      const match = bundleSku.match(/^PPPC-IWTTE2-([A-Z]+)-([A-Z]+)$/);
      if (!match) return null;
      return [match[1], match[2]];
    }

    /**
     * Build a lookup map from sorted flavor code pair to bundle variant.
     * Sorting normalizes "CHOC then VAN" and "VAN then CHOC" to the same
     * key.
     */
    buildComboMap() {
      const map = {};
      const bundleVariants = this.data.bundleVariants || [];

      bundleVariants.forEach(variant => {
        const codes = this.extractBundleFlavorCodes(variant.sku);
        if (codes) {
          const key = [...codes].sort().join('+');
          map[key] = variant;
        }
      });

      return map;
    }

    /**
     * Given two selected powder SKUs, find the matching bundle variant.
     */
    getMatchedVariant(powderSku1, powderSku2) {
      if (!powderSku1 || !powderSku2) return null;
      const code1 = this.extractFlavorCode(powderSku1);
      const code2 = this.extractFlavorCode(powderSku2);
      if (!code1 || !code2) return null;
      const key = [code1, code2].sort().join('+');
      return this.comboMap[key] || null;
    }

    /**
     * Bind event listeners for things this module owns: just the powder
     * variant buttons.
     */
    bindEvents() {
      this.variantButtons1.forEach(btn => {
        btn.addEventListener('click', () => {
          if (!btn.disabled) {
            this.selectPowder(btn, 1);
          }
        });
      });
      this.variantButtons2.forEach(btn => {
        btn.addEventListener('click', () => {
          if (!btn.disabled) {
            this.selectPowder(btn, 2);
          }
        });
      });
    }

    /**
     * Select a powder flavor for slot 1 or 2
     */
    selectPowder(button, slotNumber) {
      const variantSku = button.dataset.variantSku;
      const variantImage = button.dataset.variantImage;

      if (slotNumber === 1) {
        this.selectedPowderSku1 = variantSku;
        this.variantButtons1.forEach(btn => btn.classList.remove('selected'));
      } else {
        this.selectedPowderSku2 = variantSku;
        this.variantButtons2.forEach(btn => btn.classList.remove('selected'));
      }

      button.classList.add('selected');

      if (variantImage) {
        const productRow = button.closest('.single-product-row');
        const img = productRow && productRow.querySelector('.single-product-row__main-image');
        if (img) img.src = variantImage;
      }

      this.validateSelections();
    }

    /**
     * Validate selections + drive the buy card's enabled state, validation
     * slot, and (when a matched variant is found) the displayed variant.
     */
    validateSelections() {
      const bothSelected = this.selectedPowderSku1 !== null && this.selectedPowderSku2 !== null;
      const translations = this.data.translations || {};
      let isValid = false;
      let message = '';
      let matched = null;

      if (bothSelected) {
        matched = this.getMatchedVariant(this.selectedPowderSku1, this.selectedPowderSku2);
        if (matched && matched.available) {
          isValid = true;
        } else if (matched && !matched.available) {
          message = translations.out_of_stock || 'Out of stock';
        } else {
          message = translations.combo_unavailable || 'This combination is currently unavailable';
        }
      } else {
        message = translations.please_select_both_flavors || 'Please select both protein powder flavors';
      }

      if (this.cardApi) {
        this.cardApi.setEnabled(isValid);
        this.cardApi.setValidationMessage(message);
        // Drive the buy card's per-variant price + strikethrough display
        // when we've identified a matched variant — even if it's
        // unavailable, the customer should see the price for the combo
        // they picked.
        if (matched && matched.id) {
          this.cardApi.setVariant(matched.id);
        }
      }

      return isValid;
    }

    /**
     * CTA click handler — buy card has already gated the click on the
     * disabled state, so a matched/available variant exists. planId is ""
     * for one-time, otherwise the chosen plan ID.
     */
    async handleAddToCart(planId) {
      if (!this.validateSelections()) return;

      const matched = this.getMatchedVariant(this.selectedPowderSku1, this.selectedPowderSku2);
      if (!matched) {
        this.showError('No matching variant found');
        return;
      }

      if (this.cardApi) this.cardApi.setEnabled(false);

      try {
        await this.addVariantToCart(matched.id, planId);

        document.dispatchEvent(new CustomEvent('cart:update'));

        if (window.paCart && typeof window.paCart.refresh === 'function') {
          window.paCart.refresh();
          if (typeof window.paCart.open === 'function') window.paCart.open();
        } else {
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }
        }
      } catch (error) {
        console.error('Failed to add bundle to cart:', error);
        this.showError(error.message || 'Failed to add to cart. Please try again.');
      } finally {
        if (this.cardApi) this.cardApi.setEnabled(true);
      }
    }

    /**
     * Post the matched bundle variant to /cart/add. planId is "" for
     * one-time, "<id>" for subscribed.
     */
    async addVariantToCart(variantId, planId) {
      const isUpCartActive = !!(
        window.UpCart ||
        window.upcart ||
        window.Upcart ||
        document.querySelector('script[src*="upcart"]') ||
        document.querySelector('link[href*="upcart"]')
      );

      const cart = !isUpCartActive && (
        document.querySelector('cart-notification') ||
        document.querySelector('cart-drawer')
      );

      // Build FormData — single item, quantity 1
      const formData = new FormData();
      formData.append('items[0][id]', variantId);
      formData.append('items[0][quantity]', 1);
      if (planId) {
        formData.append('items[0][selling_plan]', planId);
      }

      let url = '/cart/add';
      if (!isUpCartActive && cart && typeof cart.getSectionsToRender === 'function') {
        const sections = cart.getSectionsToRender().map((section) => section.id).join(',');
        const sectionsUrl = window.location.pathname;
        url += `?sections=${encodeURIComponent(sections)}&sections_url=${encodeURIComponent(sectionsUrl)}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData
      });

      if (!response.ok && response.status === 400 && url.includes('sections=')) {
        const retryResponse = await fetch('/cart/add', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: formData
        });

        if (!retryResponse.ok) {
          const errorText = await retryResponse.text();
          throw new Error(errorText || 'Failed to add to cart');
        }

        return await retryResponse.json();
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to add to cart');
      }

      const data = await response.json();

      if (!isUpCartActive && cart && data.sections) {
        cart.renderContents(data);
      }

      return data;
    }

    showError(message) {
      if (this.errorMessage) {
        this.errorMessage.textContent = message;
        this.errorMessage.style.display = 'block';
        setTimeout(() => {
          this.errorMessage.style.display = 'none';
        }, 5000);
      }
    }
  }

  window.TasteThemAllBundle2Builder.init = function(containerId) {
    return new BundleBuilder(containerId);
  };

})();
